"use client"

// Small client island: a per-side "View on Amazon" chip for the (otherwise
// server-rendered) CompareVersusPanel. Kept separate so the panel's photos + alt
// text stay in crawlable server HTML while only this chip hydrates for click
// tracking. Fires affiliate_link_clicked with the `compare-versus` placement.

import { contentAnalytics, trackEvent } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { withAmazonTagForPlacement } from "@/lib/affiliate"
import type { ProductRecommendationLink } from "@/lib/product-picks/types"

// Shared surface for the impression/click pair so `compare-versus` CTR is
// computable. The legacy affiliate_link_clicked call below stays untouched.
const VERSUS_SURFACE = "compare-versus"

function IconArrow({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}

export function VersusBuyChip({
  product,
  slug,
  variant,
}: {
  product: ProductRecommendationLink
  slug: string
  variant: "a" | "b"
}) {
  // Side A is the first chip in reading order, side B the second.
  const position = variant === "a" ? 1 : 2
  const href = product.affiliateUrl ? withAmazonTagForPlacement(product.affiliateUrl, VERSUS_SURFACE) : ""
  // Hook stays above the early return so the call order is unconditional. When
  // there is no affiliate URL the ref never attaches, so nothing fires.
  const impressionRef = useAffiliateImpression(VERSUS_SURFACE, {
    article_slug: `compare:${slug}`,
    product_name: product.name,
    affiliate_link: href,
    asin: product.asin ?? undefined,
    placement: VERSUS_SURFACE,
    source_context: `versus-${variant}`,
    position,
  })

  if (!product.affiliateUrl) return null

  const handleClick = () => {
    trackEvent("affiliate_link_clicked", {
      article_slug: `compare:${slug}`,
      product_name: product.name,
      affiliate_link: href,
      placement: "compare-versus",
      asin: product.asin ?? undefined,
      source_context: `versus-${variant}`,
      position,
    })
    // Unified click on the SAME surface as the impression above.
    contentAnalytics.affiliateProductClicked(VERSUS_SURFACE, {
      productName: product.name,
      affiliateLink: href,
      asin: product.asin ?? undefined,
      article_slug: `compare:${slug}`,
      placement: VERSUS_SURFACE,
      source_context: `versus-${variant}`,
      position,
    })
  }

  return (
    <a
      ref={impressionRef}
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-affiliate="true"
      data-affiliate-placement={VERSUS_SURFACE}
      onClick={handleClick}
      className="cmp-versus__buy"
    >
      View Price on Amazon
      <IconArrow size={11} />
    </a>
  )
}
